import React, { useState } from 'react';
import { fmtDate, money } from '../finance/shared';
import { whatsappLink, copyToClipboard } from './phoneUtils';
import { subscriptionBadge, daysRemaining, progressBarColor, progressBarPercent, PAYMENT_STATUS_LABELS } from './subscriptionUtils';
import ActionsMenu from './ActionsMenu';
import Icon from '../../../components/dashboard/DashboardIcons';
import { WhatsAppMessageModal } from '../../../components/whatsapp/WhatsAppMessageModal';

const waVariablesFor = (p) => {
  const remaining = (p.subscription_amount ?? null) !== null && (p.subscription_paid_amount ?? null) !== null
    ? Math.max(p.subscription_amount - p.subscription_paid_amount, 0) : null;
  return {
    guardian_name: p.guardian_name || 'ولي الأمر',
    player_name: p.name,
    subscription_name: p.subscription_package || '',
    expiry_date: fmtDate(p.subscription_end),
    paid_amount: p.subscription_paid_amount ?? '',
    remaining_amount: remaining ?? '',
    payment_reference: p.subscription_payment_reference || '',
  };
};

const DaysBar = ({ p }) => {
  const dl = daysRemaining(p.subscription_end);
  return (
    <div className="min-w-[90px]">
      <div className="mb-1 text-[11px] font-semibold text-slate-600">{dl === null ? '—' : dl < 0 ? `منتهٍ منذ ${Math.abs(dl)} يوم` : `${dl} يوم`}</div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${progressBarColor(p)}`} style={{ width: `${progressBarPercent(p)}%` }} /></div>
    </div>
  );
};

export default function SubscriptionsTable({ players, catName, onView, onEdit, onPay, onRenew, onExtend, onPauseResume, onCancel, onActivate, canFinance, canWhatsApp }) {
  const [waPlayer, setWaPlayer] = useState(null);
  const rowActions = (p) => {
    const status = subscriptionBadge(p).key;
    const items = [
      { icon: 'users', label: 'عرض تفاصيل الاشتراك', onClick: () => onView(p), testId: 'sub-action-view' },
      { icon: 'cog', label: 'تعديل الاشتراك', onClick: () => onEdit(p), testId: 'sub-action-edit' },
      canWhatsApp && { icon: 'whatsapp', label: 'مراسلة عبر واتساب', onClick: () => setWaPlayer(p), testId: 'sub-action-whatsapp' },
    ];
    if (status === 'not_registered') return items;
    if (canFinance && ['active', 'pending_payment'].includes(status)) items.push({ icon: 'banknotes', label: 'تسجيل دفعة', onClick: () => onPay(p), testId: 'sub-action-pay' });
    if (canFinance && ['pending_payment', 'upcoming'].includes(status)) items.push({ icon: 'check', label: 'تفعيل الاشتراك', onClick: () => onActivate(p), testId: 'sub-action-activate' });
    if (canFinance && ['expired', 'cancelled'].includes(status)) items.push({ icon: 'calendar', label: 'تجديد الاشتراك', onClick: () => onRenew(p), testId: 'sub-action-renew' });
    if (canFinance && status === 'active') items.push({ icon: 'clock', label: 'تمديد مجاني', onClick: () => onExtend(p), testId: 'sub-action-extend' });
    if (canFinance && status === 'paused') items.push({ icon: 'check', label: 'استئناف الاشتراك', onClick: () => onPauseResume(p, false), testId: 'sub-action-resume' });
    else if (canFinance && status === 'active') items.push({ icon: 'x', label: 'إيقاف مؤقت', onClick: () => onPauseResume(p, true), testId: 'sub-action-pause' });
    if (canFinance && ['active', 'pending_payment', 'paused'].includes(status)) items.push({ icon: 'trash', label: 'إلغاء الاشتراك', onClick: () => onCancel(p), testId: 'sub-action-cancel', danger: true });
    return items;
  };

  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
            <th className="px-4 py-2 font-semibold">اللاعب</th>
            <th className="px-2 py-2 font-semibold">الفئة</th>
            <th className="px-2 py-2 font-semibold">حالة الاشتراك</th>
            <th className="px-2 py-2 font-semibold">البداية / النهاية</th>
            <th className="px-2 py-2 font-semibold">الأيام المتبقية</th>
            <th className="px-2 py-2 font-semibold">القيمة</th>
            <th className="px-2 py-2 font-semibold">حالة الدفع</th>
            <th className="px-2 py-2 font-semibold"></th>
          </tr></thead>
          <tbody>
            {players.map((p) => {
              const badge = subscriptionBadge(p);
              const wa = whatsappLink(p.phone || p.guardian_phone);
              return (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="subscription-row">
                  <td className="px-4 py-2">
                    <button onClick={() => onView(p)} className="text-right" data-testid="sub-view-btn">
                      <div className="font-semibold text-slate-800 hover:text-teal-700">{p.name}</div>
                      <div className="flex items-center gap-1 text-xs text-slate-500" dir="ltr">
                        <span>{p.phone || p.guardian_phone || '—'}</span>
                        {wa && <a href={wa} target="_blank" rel="noreferrer" title="واتساب" onClick={(e) => e.stopPropagation()} className="rounded p-0.5 text-teal-600 hover:bg-teal-50"><Icon name="whatsapp" className="w-3.5 h-3.5" /></a>}
                      </div>
                    </button>
                  </td>
                  <td className="px-2 py-2 text-slate-600">{catName(p.category_id)}</td>
                  <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${badge.cls}`} data-testid="sub-status-badge">{badge.label}</span></td>
                  <td className="px-2 py-2 text-xs text-slate-500">{p.subscription_start ? fmtDate(p.subscription_start) : '—'} — {p.subscription_end ? fmtDate(p.subscription_end) : '—'}</td>
                  <td className="px-2 py-2"><DaysBar p={p} /></td>
                  <td className="px-2 py-2 font-semibold text-slate-700" dir="rtl">{p.subscription_amount != null ? money(p.subscription_amount) : '—'}</td>
                  <td className="px-2 py-2 text-xs text-slate-500">{p.subscription_payment_status ? (PAYMENT_STATUS_LABELS[p.subscription_payment_status] || p.subscription_payment_status) : '—'}</td>
                  <td className="px-2 py-2"><div className="flex justify-end"><ActionsMenu items={rowActions(p)} testId="sub-actions-btn" /></div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="space-y-2 p-3 lg:hidden">
        {players.map((p) => {
          const badge = subscriptionBadge(p);
          return (
            <div key={p.id} className="rounded-xl border border-slate-200 p-3" data-testid="subscription-card-mobile">
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => onView(p)} className="min-w-0 text-right">
                  <div className="truncate font-semibold text-slate-800">{p.name}</div>
                  <div className="text-xs text-slate-500" dir="ltr">{p.phone || p.guardian_phone || '—'}</div>
                </button>
                <ActionsMenu items={rowActions(p)} testId="sub-actions-btn-mobile" />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                <span>{catName(p.category_id)}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
                <span className="mr-auto font-semibold text-slate-700">{p.subscription_amount != null ? money(p.subscription_amount) : '—'}</span>
              </div>
              <div className="mt-2"><DaysBar p={p} /></div>
            </div>
          );
        })}
      </div>

      {waPlayer && (
        <WhatsAppMessageModal
          open={Boolean(waPlayer)}
          onClose={() => setWaPlayer(null)}
          recipient={{ id: waPlayer.id, name: waPlayer.guardian_name || waPlayer.name, phone: waPlayer.guardian_phone || waPlayer.phone, type: 'player_guardian' }}
          variables={waVariablesFor(waPlayer)}
          relatedType="player"
          relatedId={waPlayer.id}
        />
      )}
    </>
  );
}
